import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, TrainFront } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/constants/brand";

const THALI_IMG = "/thali.png";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

function RevealLine({ index, children, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block ${className}`}
        custom={index}
        variants={lineVariants}
        initial="hidden"
        animate="visible"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 700], [0, -70]);
  const yGhost = useTransform(scrollY, [0, 700], [0, 110]);

  return (
    <section id="top" className="relative overflow-hidden pt-28 lg:pt-32" data-testid="hero-section">
      <motion.p
        style={{ y: yGhost }}
        className="text-outline pointer-events-none absolute -top-4 left-0 select-none whitespace-nowrap font-display text-[22vw] font-black leading-none opacity-[0.07]"
        aria-hidden="true"
      >
        SATVIK SATVIK
      </motion.p>

      <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:px-8 lg:pb-28">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-900/15 bg-white/60 px-4 py-1.5 backdrop-blur"
            data-testid="hero-eyebrow"
          >
            <TrainFront className="h-4 w-4 text-amber-600" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-900">
              No onion • No garlic • Delivered at your station
            </span>
          </motion.div>

          <h1 className="font-display text-5xl font-bold leading-[1.02] tracking-tight text-emerald-950 sm:text-6xl lg:text-7xl" data-testid="hero-heading">
            <RevealLine index={0}>Hot Jain thali,</RevealLine>
            <RevealLine index={1}>
              served at <span className="italic text-amber-600">your berth.</span>
            </RevealLine>
            <RevealLine index={2}>
              <span className="text-3xl font-semibold text-emerald-900/80 sm:text-4xl lg:text-5xl">
                Fresh. Pure. Right on time.
              </span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-emerald-950/70 sm:text-lg"
            data-testid="hero-subtext"
          >
            Travelling by train shouldn't mean compromising on your food principles. We cook a pure
            satvik Jain thali fresh for every order and hand it to you at your seat when your train
            pulls in.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#order"
              data-testid="hero-order-button"
              className="group rounded-full bg-amber-600 px-8 py-4 text-base font-bold text-amber-50 shadow-xl shadow-amber-600/30 transition-all hover:-translate-y-0.5 hover:bg-amber-700"
            >
              Order Your Thali — ₹250
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-900/25 px-7 py-[14px] text-base font-bold text-emerald-950 transition-colors hover:bg-emerald-900 hover:text-amber-50"
            >
              <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {[
              ["₹250", "full Jain thali"],
              ["150+", "stations served"],
              ["60 min", "order cutoff"],
            ].map(([big, small]) => (
              <div key={small}>
                <p className="font-display text-3xl font-bold text-emerald-950">{big}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-900/60">{small}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yImage }} className="relative mx-auto w-full max-w-md lg:max-w-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-5 rounded-full bg-amber-500/15 blur-2xl" aria-hidden="true" />
            <div className="relative aspect-square overflow-hidden rounded-full border-[10px] border-amber-500/90 shadow-2xl shadow-amber-900/30">
              <img
                src={THALI_IMG}
                alt="Jain Special Thali with dal, roti, rice and sabzis"
                className="h-full w-full object-cover"
                data-testid="hero-thali-image"
              />
            </div>
            <div className="animate-float-slow absolute -right-3 top-8 rounded-2xl border border-amber-200/60 bg-white/90 px-5 py-3 shadow-xl backdrop-blur-xl" data-testid="hero-price-badge">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-800/70">Jain Special Thali</p>
              <p className="font-display text-2xl font-bold text-emerald-950">₹250 <span className="text-sm font-semibold text-emerald-900/60">+ delivery</span></p>
            </div>
            <div className="animate-float-slow absolute -left-4 bottom-10 rounded-2xl border border-amber-200/60 bg-emerald-900 px-5 py-3 text-amber-50 shadow-xl [animation-delay:1.4s]" data-testid="hero-hot-badge">
              <p className="text-xs font-bold uppercase tracking-[0.18em]">Delivered hot</p>
              <p className="text-sm text-amber-100/80">as your train pulls in</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="flex justify-center pb-8">
        <a href="#stations" data-testid="hero-scroll-hint" aria-label="Scroll to stations">
          <ArrowDown className="h-5 w-5 animate-bounce text-emerald-900/50" />
        </a>
      </div>
    </section>
  );
}
